import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 300;

function canvasPoint(canvas, event) {
  const rect = canvas.getBoundingClientRect();
  const source = event.touches?.[0] || event;
  return {
    x: ((source.clientX - rect.left) / rect.width) * CANVAS_WIDTH,
    y: ((source.clientY - rect.top) / rect.height) * CANVAS_HEIGHT,
  };
}

export default function HeliteBuonoSignatureDialog({
  open,
  buono,
  saving = false,
  onSkip,
  onSave,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);

  const resetCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 3;
    setHasSignature(false);
  };

  useEffect(() => {
    if (!open) return;
    resetCanvas();
  }, [open, buono?.id]);

  const startDrawing = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    lastPointRef.current = canvasPoint(canvas, event);
  };

  const draw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = canvasPoint(canvas, event);
    const last = lastPointRef.current || point;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    setHasSignature(true);
  };

  const stopDrawing = (event) => {
    event?.preventDefault?.();
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;
    onSave?.({
      signatureDataUrl: canvas.toDataURL("image/png"),
      signatureWidth: CANVAS_WIDTH,
      signatureHeight: CANVAS_HEIGHT,
    });
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        {buono?.number ? `Firma Buono ${buono.number}` : "Firma Buono"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {[buono?.heliteRoomNumber, buono?.heliteHolderName].filter(Boolean).join(" - ") || "-"}
          </Typography>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "#fff",
              height: { xs: 220, sm: 300 },
              touchAction: "none",
            }}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                cursor: "crosshair",
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetCanvas} disabled={saving}>
          Pulisci
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onSkip} disabled={saving}>
          Salta
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !hasSignature}>
          Salva firma
        </Button>
      </DialogActions>
    </Dialog>
  );
}
